/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Button, IconButton, Tooltip } from '@material-ui/core';
import { Check, Delete, Undo } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Input } from '~/components/Form';
import { t } from '~/helpers';

import { Container, DrawingToolbar, MapWrapper, MyForm } from './styles';

const DEFAULT_CENTER = { lat: -19.9084242, lng: -43.916540900000008 };
const MAP_SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
const MAP_READY_CALLBACK = '__scaplusGoogleMapsReady';

let googleMapsApiPromise;

export function loadGoogleMapsApi() {
  if (window.google && window.google.maps && window.google.maps.Map) {
    return Promise.resolve(window.google.maps);
  }

  if (googleMapsApiPromise) {
    return googleMapsApiPromise;
  }

  googleMapsApiPromise = new Promise((resolve, reject) => {
    let resolved = false;
    let readyInterval;
    let readyTimeout;

    const clearWaiters = () => {
      clearInterval(readyInterval);
      clearTimeout(readyTimeout);
    };

    const resolveApi = () => {
      if (window.google && window.google.maps && window.google.maps.Map) {
        resolved = true;
        clearWaiters();
        resolve(window.google.maps);
      }
    };

    const rejectApi = () => {
      if (!resolved) {
        clearWaiters();
        googleMapsApiPromise = null;
        reject(new Error('Google Maps API unavailable'));
      }
    };

    const currentScript = document.querySelector(MAP_SCRIPT_SELECTOR);

    readyInterval = setInterval(resolveApi, 100);
    readyTimeout = setTimeout(rejectApi, 10000);
    window[MAP_READY_CALLBACK] = resolveApi;

    if (currentScript) {
      currentScript.addEventListener('load', resolveApi, { once: true });
      currentScript.addEventListener('error', rejectApi, { once: true });
      resolveApi();
      return;
    }

    const script = document.createElement('script');

    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places,drawing&loading=async&callback=${MAP_READY_CALLBACK}`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', resolveApi, { once: true });
    script.addEventListener('error', rejectApi, { once: true });

    document.body.appendChild(script);
  });

  return googleMapsApiPromise;
}

function createMap(element) {
  const map = new window.google.maps.Map(element, {
    zoom: 12,
    center: DEFAULT_CENTER,
    mapTypeId: 'satellite',
  });

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition((position) => {
      map.setCenter({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    });
  }

  return map;
}

function serializePath(path, zoom) {
  return {
    zoommapacercavirtual: zoom,
    itens: path.map((coord, index) => ({
      sequenciacercavirtualitem: Number(index + 1),
      latitudecercavirtualitem: coord.lat(),
      longitudecercavirtualitem: coord.lng(),
    })),
  };
}

function polygonOptions(editable) {
  return {
    fillColor: '#ff6666',
    fillOpacity: 0.35,
    strokeColor: '#ff6666',
    strokeWeight: 3,
    editable,
  };
}

function refreshMapLayout(map) {
  const center = map.getCenter();

  window.google.maps.event.trigger(map, 'resize');

  if (center) {
    map.setCenter(center);
  }
}

export default function Maps({ callback, initials, edit, visible }) {
  const mapElement = useRef(null);
  const maps = useRef(null);
  const polygon = useRef(null);
  const polygonPath = useRef([]);
  const drawingListeners = useRef([]);
  const refreshTimers = useRef([]);
  const openRef = useRef(false);
  const formRef = useRef(null);

  const { client } = useSelector((state) => state.user.profile);

  const [data, setData] = useState({});
  const [mapReady, setMapReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [vertexCount, setVertexCount] = useState(0);

  /**
   * remove map listeners
   */
  function clearDrawingListeners() {
    drawingListeners.current.forEach((listener) =>
      window.google.maps.event.removeListener(listener)
    );
    drawingListeners.current = [];
  }

  /**
   * clear scheduled map refreshes
   */
  function clearRefreshTimers() {
    refreshTimers.current.forEach((timer) => clearTimeout(timer));
    refreshTimers.current = [];
  }

  /**
   * refresh map when the dialog is visible
   */
  function scheduleMapRefresh() {
    clearRefreshTimers();

    refreshTimers.current = [0, 300, 700].map((delay) =>
      setTimeout(() => {
        if (maps.current) {
          refreshMapLayout(maps.current);
        }
      }, delay)
    );
  }

  /**
   * draw polygon in map
   *
   * @param {Array} path
   * @param {Boolean} editable
   */
  function drawPolygon(path = [], editable = true) {
    polygonPath.current = path;

    if (polygon.current) {
      polygon.current.setMap(null);
    }

    polygon.current = new window.google.maps.Polygon({
      ...polygonOptions(editable),
      paths: path,
      map: maps.current,
    });

    setVertexCount(path.length);
  }

  /**
   * add polygon point
   *
   * @param {Object} latLng
   */
  function addVertex(latLng) {
    if (openRef.current) {
      return;
    }

    const path = [...polygonPath.current, latLng];

    if (!polygon.current) {
      drawPolygon(path);
      return;
    }

    polygonPath.current = path;
    polygon.current.setPath(path);
    setVertexCount(path.length);
  }

  /**
   * undo last polygon point
   */
  function handleUndoVertex() {
    if (!polygon.current || openRef.current) {
      return;
    }

    const path = polygonPath.current.slice(0, -1);

    polygonPath.current = path;
    polygon.current.setPath(path);
    setVertexCount(path.length);
  }

  /**
   * reset polygon drawing
   */
  function handleClearPolygon() {
    if (polygon.current) {
      polygon.current.setMap(null);
      polygon.current = null;
    }

    polygonPath.current = [];
    openRef.current = false;
    setData({});
    setOpen(false);
    setVertexCount(0);

    if (maps.current && !edit) {
      maps.current.setOptions({
        draggableCursor: 'crosshair',
        disableDoubleClickZoom: true,
      });
    }
  }

  /**
   * confirm polygon points
   */
  function handleConfirmPolygon() {
    if (!polygon.current || vertexCount < 3) {
      return;
    }

    const path = polygonPath.current;

    polygon.current.setOptions({
      clickable: false,
      editable: false,
    });

    setData(serializePath(path, maps.current.getZoom()));
    openRef.current = true;
    setOpen(true);

    maps.current.setOptions({
      draggableCursor: null,
      disableDoubleClickZoom: false,
    });
  }

  /**
   * submit form
   * @param {Object} params
   */
  async function handleSubmit(params) {
    try {
      const schema = Yup.object().shape({
        descricaocercavirtual: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
      });

      await schema.validate(params, {
        abortEarly: false,
      });

      const polygonData =
        edit && polygon.current
          ? serializePath(
              polygon.current.getPath().getArray(),
              maps.current.getZoom()
            )
          : data;

      const zoommapacercavirtual =
        polygonData.zoommapacercavirtual || initials.zoom || '';

      const param = {
        zoommapacercavirtual,
        ...params,
        ...polygonData,
        idcliente: client,
      };

      callback(param);
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        formRef.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    let mounted = true;

    loadGoogleMapsApi().catch(() => {
      if (mounted) {
        setMapReady(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!visible || maps.current) {
      return undefined;
    }

    let mounted = true;

    setMapReady(false);

    loadGoogleMapsApi()
      .then(() => {
        if (!mounted || !mapElement.current) {
          return;
        }

        maps.current = createMap(mapElement.current);
        scheduleMapRefresh();
        setMapReady(true);

        if (!edit) {
          maps.current.setOptions({
            draggableCursor: 'crosshair',
            disableDoubleClickZoom: true,
          });

          drawingListeners.current = [
            maps.current.addListener('click', ({ latLng }) => {
              if (latLng) {
                addVertex(latLng);
              }
            }),
          ];
        }
      })
      .catch(() => {
        if (mounted) {
          setMapReady(false);
        }
      });

    return () => {
      mounted = false;
      clearDrawingListeners();
      clearRefreshTimers();

      if (polygon.current) {
        polygon.current.setMap(null);
        polygon.current = null;
      }

      maps.current = null;
    };
  }, [edit, visible]);

  useEffect(() => {
    if (visible && mapReady && maps.current) {
      scheduleMapRefresh();
    }
  }, [mapReady, visible]);

  useEffect(() => {
    if (
      visible &&
      mapReady &&
      maps.current &&
      !isEmpty(initials) &&
      !isEmpty(initials.items)
    ) {
      const { latitudecercavirtualitem, longitudecercavirtualitem } = [
        ...initials.items,
      ].shift();
      const path = initials.items.map((item) => ({
        lat: item.latitudecercavirtualitem,
        lng: item.longitudecercavirtualitem,
      }));

      setOpen(true);
      maps.current.setCenter({
        lat: latitudecercavirtualitem,
        lng: longitudecercavirtualitem,
      });
      maps.current.setZoom(initials.zoom);
      drawPolygon(path, edit);
      scheduleMapRefresh();
    }
  }, [edit, initials, mapReady, visible]);

  return (
    <>
      {(!edit || (edit && !isEmpty(initials))) && (
        <MyForm
          ref={formRef}
          onSubmit={handleSubmit}
          open={open}
          initialData={initials.form}
        >
          <Input
            name="descricaocercavirtual"
            label={t('s.configuracao.cercavirtual.descricaocercavirtual')}
          />

          <Button
            startIcon={<Check />}
            color="primary"
            variant="outlined"
            onClick={() => formRef.current.submitForm()}
          >
            {t('s.geral.botaoconfirmar')}
          </Button>
        </MyForm>
      )}

      <MapWrapper>
        {!edit && mapReady && (
          <DrawingToolbar>
            <Tooltip title="Confirmar poligono">
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  disabled={vertexCount < 3 || open}
                  onClick={handleConfirmPolygon}
                >
                  <Check />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Desfazer ultimo ponto">
              <span>
                <IconButton
                  size="small"
                  disabled={vertexCount === 0 || open}
                  onClick={handleUndoVertex}
                >
                  <Undo />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t('s.geral.botaoexcluir')}>
              <span>
                <IconButton
                  color="secondary"
                  size="small"
                  disabled={vertexCount === 0}
                  onClick={handleClearPolygon}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </DrawingToolbar>
        )}

        <Container ref={mapElement} open={open} />
      </MapWrapper>
    </>
  );
}

Maps.propTypes = {
  callback: PropTypes.func.isRequired,
  initials: PropTypes.shape({
    zoom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        latitudecercavirtualitem: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
        longitudecercavirtualitem: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
      })
    ),
    form: PropTypes.shape({
      descricaocercavirtual: PropTypes.string,
    }),
  }),
  edit: PropTypes.bool,
  visible: PropTypes.bool,
};

Maps.defaultProps = {
  initials: {},
  edit: false,
  visible: true,
};
